import RestoreIcon from "@mui/icons-material/Restore"
import Button from "@mui/material/Button"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import Chip from "@mui/material/Chip"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"

import type {
  LiveChatWidgetRelease,
  LiveChatWidgetReleaseStatus
} from "@models/live_chat_widget"
import { formatDate } from "@utils/functions/formatDate"

const STATUS_COLOR: Record<
  LiveChatWidgetReleaseStatus,
  "success" | "info" | "warning" | "error" | "default"
> = {
  ACTIVE: "success",
  INVALIDATING: "info",
  INVALIDATION_FAILED: "warning",
  UPLOADING: "info",
  SUPERSEDED: "default",
  FAILED: "error"
}

export default function ReleaseHistory({
  translate,
  releases,
  busy,
  canWrite,
  onRollback
}: {
  translate: (key: string) => string
  releases: LiveChatWidgetRelease[]
  busy: boolean
  canWrite: boolean
  onRollback: (release: LiveChatWidgetRelease) => void
}) {
  return (
    <Card variant="outlined">
      <CardContent>
        <Stack gap={1.5}>
          <Typography variant="subtitle1">
            {translate("live_chat_widget_history_title")}
          </Typography>

          {releases.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              {translate("live_chat_widget_history_empty")}
            </Typography>
          )}

          {releases.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate("live_chat_widget_date")}</TableCell>
                  <TableCell>
                    {translate("live_chat_widget_version_label")}
                  </TableCell>
                  <TableCell>{translate("live_chat_widget_status")}</TableCell>
                  <TableCell align="right">
                    {translate("live_chat_widget_file_count")}
                  </TableCell>
                  <TableCell>{translate("live_chat_widget_health")}</TableCell>
                  <TableCell>
                    {translate("live_chat_widget_published_by")}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {releases.map((release) => {
                  const status = release.status ?? "UPLOADING"
                  const healthy = release.healthCheckStatus === 200
                  const restorable =
                    !!release.archivePrefix && status !== "ACTIVE"

                  return (
                    <TableRow key={release._id}>
                      <TableCell>
                        {release.createdAt
                          ? formatDate("tr-TR", release.createdAt)
                          : "—"}
                      </TableCell>
                      <TableCell>
                        <Stack gap={0.25}>
                          <Typography variant="body2">
                            {release.version || "—"}
                          </Typography>
                          {!!release.note && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {release.note}
                            </Typography>
                          )}
                          {release.source === "ROLLBACK" && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {translate("live_chat_widget_source_rollback")}
                            </Typography>
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Tooltip title={release.errorMessage || ""}>
                          <Chip
                            size="small"
                            color={STATUS_COLOR[status]}
                            label={translate(
                              `live_chat_widget_status_${status.toLowerCase()}`
                            )}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {release.files?.length ?? 0}
                      </TableCell>
                      <TableCell>
                        {release.healthCheckStatus ? (
                          <Chip
                            size="small"
                            color={healthy ? "success" : "error"}
                            variant="outlined"
                            label={`HTTP ${release.healthCheckStatus}`}
                          />
                        ) : (
                          "—"
                        )}
                      </TableCell>
                      <TableCell>{release.publishedBy || "—"}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          startIcon={<RestoreIcon />}
                          disabled={busy || !canWrite || !restorable}
                          onClick={() => onRollback(release)}
                        >
                          {translate("live_chat_widget_rollback")}
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </Stack>
      </CardContent>
    </Card>
  )
}
